import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from "@angular/core";
import { NgApexchartsModule } from "ng-apexcharts";

@Component({
  selector: "app-donut-chart",
  templateUrl: "./donut-chart.component.html",
  styleUrls: ["./donut-chart.component.scss"],
  imports: [CommonModule, NgApexchartsModule],
  standalone: true,
})
export class DonutChartComponent implements OnChanges {
  @Output() dataEvent = new EventEmitter<any>();
  @Input() data: { label: string; count: number }[] = [];

  chartOptions: any = null;
  formattedData: { label: string; count: number; color: string }[] = [];
  isNoData: boolean = true;

  @Input() colorMap: { [label: string]: string } = {}; // label -> color
  @Input() defaultColors: string[] = [
    "#E3000C",
    "#FFC300",
    "#4390E3",
    "#7CD169",
    "#FF7F50",
    "#9370DB",
  ];

  ngOnChanges(): void {
    this.prepareChart();
  }

  private prepareChart() {
    const normalizedData = Array.isArray(this.data)
      ? this.data.map((item) => ({
          label: item?.label ?? "Unknown",
          count: Number.isFinite(Number(item?.count)) ? Number(item.count) : 0,
        }))
      : [];

    const isEmpty = normalizedData.length === 0;
    const allZero = normalizedData.every((item) => item.count === 0);
    this.isNoData = isEmpty || allZero;

    this.formattedData = (isEmpty ? [] : normalizedData).map((item, index) => ({
      ...item,
      color:
        this.colorMap[item.label] ||
        this.defaultColors[index % this.defaultColors.length],
    }));

    if (this.isNoData) {
      this.chartOptions = null;
      return;
    }

    this.chartOptions = {
      chart: {
        animations: {
          enabled: false,
          speed: 0,
          animateGradually: { enabled: false },
          dynamicAnimation: { enabled: false },
        },
        fontFamily: "inherit",
        foreColor: "inherit",
        height: "150px",
        type: "donut",
        sparkline: { enabled: true },
        events: {
          dataPointSelection: (_event, _chartContext, config) => {
            const selectedIndex = config.dataPointIndex;
            const label = this.chartOptions.labels[selectedIndex];
            const value = this.chartOptions.series[selectedIndex];
            this.dataEvent.emit({
              label,
              value,
              selectedIndex,
            });
          },
        },
      },
      stroke: {
        show: false,
      },
      colors: this.formattedData.map((item) => item.color),
      labels: this.formattedData.map((item) => item.label),
      plotOptions: {
        pie: {
          customScale: 0.92,
          expandOnClick: false,
          donut: { size: "58%" },
        },
      },
      series: this.formattedData.map((item) => item.count),
      states: {
        hover: { filter: { type: "none" } },
        active: { filter: { type: "none" } },
      },
      tooltip: {
        enabled: true,
        fillSeriesColor: false,
        theme: "dark",
        custom: ({ seriesIndex, w }) => `
          <div class="flex items-center h-8 min-h-8 max-h-8 px-3">
            <div class="w-3 h-3 rounded-full" style="background-color: ${w.config.colors[seriesIndex]};"></div>
            <div class="ml-2 text-md leading-none">${w.config.labels[seriesIndex]}:</div>
            <div class="ml-2 text-md font-bold leading-none">${w.config.series[seriesIndex]}</div>
          </div>`,
      },
    };
  }

  onChartClick(label, selectedIndex) {
    if (this.isNoData) return;
    this.dataEvent.emit({
      label,
      value: label,
      selectedIndex,
    });
  }
}
